import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useSingleImageUrl } from '@blaze-cms/image-cdn-react';
import { useStringTemplate } from '@blaze-cms/utils-handlebars';
import Wrapper from '../Wrapper';
import { getClassModifiers } from '../../utils';
import { useGetImages } from '../../hooks';
import { getStylesToUpdate, checkIfRowHasColumns, getWebpPath } from './helpers';
import { renderChildren, hasChildren, getCustomHtmlProperties } from '../../helpers';
import { COLUMN, BACKGROUND } from '../../constants';

const Layout = React.forwardRef(
  (
    {
      type,
      children,
      settings,
      modifier,
      backgroundImage,
      backgroundImageUrl,
      parent,
      tagType,
      dataNoSnippet,
      sticky,
      priority,
      VariantComponent,
      customHtmlProps = [],
      ...otherProps
    },
    ref
  ) => {
    const {
      data: [resolvedBackgroundImageUrl]
    } = useStringTemplate(parent, [backgroundImageUrl || '']);
    const {
      data: { getFile: { url = null } = {} }
    } = useGetImages(backgroundImage, false, !!resolvedBackgroundImageUrl);
    const backgroundImageSrc = resolvedBackgroundImageUrl || url;
    // the cdn rewrites paths on the source origin, so a background hosted somewhere that
    // does not serve `/convert` will 404. a css background has no `<picture>` fallback to
    // recover with, so only point `backgroundImageUrl` at an origin backed by the cdn
    const convertedUrl = useSingleImageUrl({ src: backgroundImageSrc, sizeKey: BACKGROUND });
    const backgroundUrl = getWebpPath(backgroundImageSrc, convertedUrl);
    // the preload must point at the same url the css requests, or the image downloads twice
    const shouldPreloadBackground = !!priority && !!backgroundUrl;
    const style = getStylesToUpdate({ backgroundImage: backgroundUrl });
    const { title } = settings;
    const classModifiers = getClassModifiers(type, { modifier, sticky, ...otherProps });
    const additionalRowModifier = checkIfRowHasColumns(type, children) ? ' display-row' : '';

    if (type === COLUMN && !hasChildren(children)) return null;

    const otherWrapperProps = {};
    if (dataNoSnippet) otherWrapperProps['data-nosnippet'] = true;

    const customHtmlProperties = getCustomHtmlProperties({ customHtmlProps });

    const WrapperComponent = VariantComponent || Wrapper;

    return (
      <>
        {shouldPreloadBackground && (
          <Head>
            <link
              // next/head dedupes by key, so nested rows sharing a background emit one tag
              key={`${BACKGROUND}-preload-${backgroundUrl}`}
              rel="preload"
              as="image"
              // lowercase renders correctly and warns on neither react 18.2 nor 18.3, while
              // camelCase `fetchPriority` warns on 18.2, which the peer range still allows
              // eslint-disable-next-line react/no-unknown-property
              fetchpriority="high"
              href={backgroundUrl}
            />
          </Head>
        )}
        <WrapperComponent
          ref={ref}
          tagType={tagType}
          className={type}
          modifiers={`${classModifiers}${additionalRowModifier}`}
          style={style}
          {...otherWrapperProps}
          {...customHtmlProperties}>
          {title && <h2 className="heading heading--section">{title}</h2>}
          {renderChildren(children, otherProps)}
        </WrapperComponent>
      </>
    );
  }
);

Layout.propTypes = {
  type: PropTypes.string.isRequired,
  sticky: PropTypes.bool,
  settings: PropTypes.shape({
    title: PropTypes.string
  }),
  modifier: PropTypes.string,
  backgroundImage: PropTypes.string,
  backgroundImageUrl: PropTypes.string,
  parent: PropTypes.object,
  tagType: PropTypes.string,
  dataNoSnippet: PropTypes.bool,
  priority: PropTypes.bool,
  VariantComponent: PropTypes.func,
  customHtmlProps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired
};

Layout.defaultProps = {
  settings: {
    title: ''
  },
  backgroundImage: '',
  backgroundImageUrl: '',
  parent: {},
  modifier: '',
  tagType: '',
  dataNoSnippet: false,
  sticky: false,
  priority: false,
  VariantComponent: null,
  customHtmlProps: []
};

export default Layout;
